const { addDays } = require('date-fns')
const get = require('lodash/get')
const { applyPayments } = require('./payment-application')
const { getApplicationStatus } = require('../../credit-application/tools')
const { MEMBERSHIPS } = require('../../location/tools')
const { getListArgs } = require('../../tools/sql-tools')

const SHIPMENT_WINDOW = 8 // weeks

/**
 * @param {string} dateFilter
 */
const statementQuery = (dateFilter) => `
  with customer as (
    select fin.get_location_customer($1)
  ),
  paymentplans as (
    select fin.get_customer_paymentplans(
      (select * from customer)
    )
  ),
  pending_payments as (
    select fin.get_customer_pending_payments(
      (select * from customer)
    )
  ),
  balance as (
    select fin.get_customer_balance(
        (select * from customer)
      , (select * from paymentplans)
      , (select * from pending_payments)
    )
  ),
  transactions AS (
    select t.*
    from fin.get_customer_transactions(
      (select * from customer),
      (select * from paymentplans)
    ) AS txn_array,
    lateral unnest(txn_array) AS t
  )
    select
      to_json((select * from customer)) as customer
       , to_json((select * from balance)) as balance
       , coalesce(
          (select jsonb_agg(t) from (select *  from transactions ${dateFilter ? ` where ${dateFilter}` : ''}) t),
          '[]'::jsonb
        ) as transactions
      , json_build_object(
          'totalInflows', coalesce((select sum(amount)::float from transactions where entry_type = 'credit' and txn_type not in ('Payment Plan', 'Invoice to Payment Plan') ), 0),
          'totalOutflows', coalesce((select sum(amount)::float from transactions where entry_type = 'debit' and txn_type not in ('Payment Plan', 'Invoice to Payment Plan') ), 0)
        ) as "account_flows"
`

const walletQuery = `
  with customer as (
    select fin.get_location_customer($1)::fin.location_customer_type as lc
  ),
  paymentplans as (
    select fin.get_customer_paymentplans(
      (select * from customer)
    )
  ),
  pending_payments as (
    select fin.get_customer_pending_payments(
      (select * from customer)
    )
  ),
  balance as (
    select fin.get_customer_balance(
        (select * from customer)
      , (select * from paymentplans)
      , (select * from pending_payments)
    )
  ),
  invoices as (
    select fin.get_customer_invoices(
      (select * from customer)
    )
  ),
  invoice_payments as (
    select fin.get_customer_invoice_payments(
      (select * from invoices)
    )
  ),
  installments as (
    select fin.get_customer_installments(
      (select * from paymentplans)
    )
  ),
  installment_payments as (
    select fin.get_customer_installment_payments(
        (select * from paymentplans)
      , (select * from installments)
    )
  ),
  invoice_late_fees as (
    select fin.get_customer_invoice_late_fees(
      (select * from customer)
    )
  ),
  payment_plan_service_fees as (
    select fin.get_customer_paymentplan_service_fees(
      (select * from customer)
    )
  ),
  installment_late_fees as (
    select fin.get_customer_installment_late_fees(
        (select * from customer)
      , (select * from installments)
    )
  ),
  invoice_shipments as (
    select fin.get_invoice_shipments(
        (select * from customer)
      , (select * from invoices)
    )
  ),
  creditmemos as (
    select 
      cm.*
    from customer c
    inner join fin.creditmemo cm ON
      (c.lc).origin = cm.origin and
      (c.lc).company = cm.company and
      (c.lc).customer_id = cm.customer_id 
  ),
  creditmemo_payment_txns as (
    select
      pl.origin,
      pl.company,
      pl.txn_id as payment_txn_id,
      pl.linked_id as creditmemo_id,
      c.txn_date
    from creditmemos c
    inner join fin.payment_line pl on c.company = pl.company and c.origin = pl.origin and c.txn_id = pl.linked_id 
    where pl.linked_type = 'creditmemo'
    group by 1, 2, 3, 4, 5
  ),
  creditmemo_linked_invoices as (
    select 
      pl.linked_id as invoice_id,
      cp.origin,
      cp.company,
      cp.creditmemo_id,
      cp.txn_date,
      pl.amount
    from creditmemo_payment_txns cp
    left join fin.payment_line pl on cp.origin = pl.origin and cp.company = pl.company and cp.payment_txn_id = pl.txn_id
    where pl.linked_type = 'invoice'
  ),
  creditmemo_shipments as (
    select 
        c.origin
      , c.company
      , c.creditmemo_id
      , c.shipment_id as return_shipment_id
      , c.order_id
      ,  c.parent_doc_id as parent_shipment_id
    from (
      select c.txn_id as creditmemo_id
        , c.origin
        , c.company
        , l.shipment_id
        , l.order_id
        , ss.parent_doc_id
      from creditmemos c
      left join avocado.data_invoice_quickbooks ql
             on ql.creditmemo_txn_id = c.txn_id::text
            and ql.company_code = c.company::text
      left join avocado.data_invoice_line l
             on l.invoice_id = ql.invoice_id
      left join avocado.data_shipmentsnapshot ss on ss.shipment_id = l.shipment_id and ss.status = 'new'
      group by 1, 2, 3, 4, 5, 6
    ) c
  ),
  has_60_90_day_options as (
    select
      distinct po.location_id
    from customer c
    inner join avocado.payment_plan_options po on po.location_id = (c.lc).location_uuid
    where frequency in ('BIMONTHLY', 'TRIMONTHLY')
  )
  select to_json((select * from customer)) as customer
       , to_json((select * from balance)) as balance
       , to_json((select * from paymentplans)) as paymentplans
       , to_json((select * from pending_payments)) as pending_payments
       , to_json((select * from invoices)) as invoices
       , to_json((select * from invoice_payments)) as invoice_payments
       , to_json((select * from invoice_late_fees)) as invoice_late_fees
       , to_json((select * from installments)) as installments
       , to_json((select * from installment_payments)) as installment_payments
       , to_json((select * from installment_late_fees)) as installment_late_fees
       , to_json((select * from payment_plan_service_fees)) as payment_plan_service_fees
       , to_json((select * from invoice_shipments)) as invoice_shipments
       , coalesce((select jsonb_agg(c) from (select * from creditmemos) c), '[]'::jsonb) as creditmemos
       , coalesce((select jsonb_agg(c) from (select * from creditmemo_linked_invoices) c), '[]'::jsonb) as creditmemo_linked_invoices
       , coalesce((select jsonb_agg(c) from (select * from creditmemo_shipments) c), '[]'::jsonb) as creditmemo_shipments
       , json_build_object(
           'has_60_day_option', coalesce(
             (select count(1) from has_60_90_day_options) > 0,
             false
           ),
           'has_90_day_option', coalesce(
             (select count(1) from has_60_90_day_options) > 0,
             false
           )
         ) as payment_options
`

const payablesQuery = `
  with customer as (
    select fin.get_location_customer($1)
  ),
  paymentplans as (
    select fin.get_customer_paymentplans(
      (select * from customer)
    )
  ),
  pending_payments as (
    select fin.get_customer_pending_payments(
      (select * from customer)
    )
  ),
  invoices as (
    select fin.get_customer_invoices(
        (select * from customer)
      , true
    )
  ),
  installments as (
    select fin.get_customer_installments(
        (select * from paymentplans)
      , true
    )
  )
  select to_json((select * from paymentplans)) as paymentplans
       , to_json((select * from pending_payments)) as pending_payments
       , to_json((select * from invoices)) as invoices
       , to_json((select * from installments)) as installments
`

const getTransactionQuery = `
  with customer as (
    select origin, company, customer_id, location_uuid as location_id from fin.get_location_customer($1)
  )
  select
    i."txn_type",
    i.txn_id,
    i.company as company_code,
    i.txn_id as id,
    i.balance::float as balance,
    doc_no,
    location_id
  from customer c
  left join fin.invoice i on c.customer_id = i.customer_id and  c.company = i.company and c.origin = i.origin
  where i.txn_id = $2

  union all

  select
    cm."txn_type",
    cm.txn_id,
    cm.company as company_code,
    cm.txn_id as id,
    cm.unapplied_amount::float as balance,
    doc_no,
    location_id
  from customer c
  left join fin.creditmemo cm on c.customer_id = cm.customer_id and c.company = cm.company and c.origin = cm.origin
  where cm.txn_id = $2
`

const invoicePaymentPlanQuery = `
  select
    pp.*,
    i.txn_id,
    i.company as company_code,
    i.doc_no as quickbooks_doc_number,
    i.txn_type
  from avocado.data_invoicepaymentplan ip
  left JOIN avocado.data_paymentplan pp on ip.paymentplan_id = pp.id
  left JOIN fin.invoice i on ip.qbo_txn_id = i.txn_id::text AND ip.qbo_company_code = i.company::text
  where ip.qbo_txn_id = $1 AND ip.qbo_company_code = $2 AND coalesce(ip._deleted, false) is not true
`

const depositLinesQuery = `
  select
    account_id::text as "accountId",
    description,
    amount::float
  from fin.deposit_line
  where account_id = any($1) and company = $2 and customer_id = $3 and origin = 'qbo'
`

const singleLRFeeBookingQuery = (accountIds, feeAccountId, companyCode, customerId) => {
  const {args, nextOffset: companyCodeOffset} = getListArgs(accountIds)
  const feeIdOffset = companyCodeOffset + 1
  const customerOffset = feeIdOffset + 1
  return {
    query: `
      with account_deposits as (
        select
          dl.txn_id,
          dl.company
        from fin.deposit_line dl
        where account_id in (${args}) and company = $${companyCodeOffset} and customer_id = $${customerOffset}
        group by 1, 2
      ),
      max_line_bookings as (
        select
          ad.txn_id,
          ad.company,
          max(dl.line_num) as max_line_num
        from account_deposits ad
        left join fin.deposit_line dl on dl.txn_id = ad.txn_id and dl.company = ad.company
        group by 1, 2
        having max(dl.line_num) <= 2
      )
      select
        dlf.account_id as "accountId",
        dlf.amount::float as amount,
        concat('LR:', dl.account_id) as description
      from max_line_bookings lb
      left join fin.deposit_line dlf on lb.txn_id = dlf.txn_id and lb.company = dlf.company
      left join fin.deposit_line dl on lb.txn_id = dl.txn_id and lb.company = dl.company and dl.line_num != dlf.line_num 
      where dlf.account_id = $${feeIdOffset} and dlf.company = $${companyCodeOffset} and dlf.description is null
    `,
    values: [...accountIds, companyCode, feeAccountId, customerId]
  }
}

const overduePAYGInvoicesQuery = `
  with overdue_invoices as (
    select
      l.name_of_pharmacy as name,
      l.fsid,
      l.classification,
      l.uuid as location_id,
      l.pay_per_use,
      l.fee_percentage,
      l.fee_percentage_long_term,
      i.company as company_code,
      i.txn_id,
      i.doc_no as quickbooks_doc_number,
      i.due_date,
      i.amount,
      coalesce(i.amount - i.balance, 0) as amount_paid,
      pp.quickbooks_account_id,
      iq.invoice_id
    from fin.invoice i
    left join avocado.data_quickbookslocation ql on ql.quickbooks_customer_id = i.customer_id::text and ql."company_code" = i.company::text
    left join avocado.data_location l on l."uuid" = ql.location_id 
    left join avocado.data_invoicepaymentplan ip on ip.qbo_txn_id = i.txn_id::text and ip.qbo_company_code = i.company::text
    left join avocado.data_paymentplan pp on pp.id = ip.paymentplan_id 
    left join avocado.data_invoice_quickbooks iq on iq.invoice_txn_id = i.txn_id::text and iq."company_code" = i.company::text
    where
     l.membership_type IN ($1, $2) and i.order_type = $3 and now() > i.due_date and
     (
       -- Return overdue PAYG invoices in need of loan conversion
       ( coalesce(i.balance, 0) > 0  and coalesce(pp.is_active, false) = false )
         OR
        -- Return overdue PAYG invoices that have been paid off, with a linked loan account and in need of payment plan activation
       ( coalesce(i.balance, 0) <= 0 and coalesce(pp.is_active, false) = false and pp.quickbooks_account_id is not null
         and not exists (
           select 1 from fin.payment_line pl
           inner join fin.payment p
             on p.txn_id = pl.txn_id
             and p.company = pl.company
             and p.origin = pl.origin
           where pl.linked_id = i.txn_id
             and pl.linked_type = 'invoice'
             and pl.company = i.company
             and pl.origin = i.origin
             and p.amount > 0
         )
       )
     )
  ),
  valid_invoice_lines as (
    select
      distinct on (il.invoice_id)
      il.invoice_id, il.order_id, il.shipment_id
    from avocado.data_invoice_line il
    where coalesce(il.event_type, '')  != 'fee' 
  ),
  valid_shipment_lines as (
    select
      distinct on (shipment_id)
      shipment_id, order_id
    from avocado.data_shipment s
    where s.status = 'received'
  ),
  valid_lines as (
    select
      oi.txn_id,
      oi."company_code",
      vi.order_id,
      oi.location_id
    from overdue_invoices oi
    left join valid_invoice_lines vi on vi.invoice_id = oi.invoice_id 
    left join valid_shipment_lines vs on vs.order_id = vi.order_id or vs.shipment_id = vi.shipment_id
    -- We only want invoices linked to shipments that have been received
    where vs.shipment_id is not null
    group by 1, 2, 3, 4
  ),
  payment_options as (
    select
      vl.txn_id,
      vl.company_code,
      po.*
    from valid_lines vl
    inner join avocado.payment_plan_options po on po.order_id = vl.order_id and po.location_id = vl.location_id 
    group by vl.txn_id, vl.company_code, po.id
  )
  select
    oi.invoice_id as id,
    oi."name",
    oi.fsid,
    oi.classification,
    oi.location_id,
    oi.pay_per_use,
    oi.fee_percentage,
    oi.fee_percentage_long_term,
    oi.txn_id,
    oi.company_code,
    oi.quickbooks_doc_number,
    oi.due_date,
    oi.amount,
    oi.amount_paid,
    oi.quickbooks_account_id,
    case
      when coalesce(po.id::text, '') != '' then json_build_object(
        'id', po.id,
        'order_id', po.order_id,
        'service_fee_percentage', po.service_fee_percentage::float,
        'frequency', po.frequency,
        'term', po.term::int,
        'location_id', po.location_id,
        'invoice_txn_id', po.txn_id,
        'company_code',po.company_code
      )
      else '{}'::json
    end as "paymentOption"
  from valid_lines vl
  left join overdue_invoices oi on vl.txn_id = oi.txn_id and vl.company_code = oi.company_code
  left join payment_options po on po.txn_id = vl.txn_id and po.company_code = vl.company_code
`

function transformPgResponse (pgResp, pendingDeliveries = [], pendingOrders = []) {
  const data = {}
  const { pendingDeliveriesAmount, loanDeliveryAmountUsed } = [...pendingDeliveries, ...pendingOrders]
    .reduce((acc, pendingShipment) => {
      if (!pendingShipment.deliveryInvoiceAmount) {
        acc.loanDeliveryAmountUsed += pendingShipment.deliveryAmount
      }
      acc.pendingDeliveriesAmount += pendingShipment.deliveryAmount
      return acc
    }, { pendingDeliveriesAmount: 0, loanDeliveryAmountUsed: 0 })

  const membershipType = pgResp.customer
    ? pgResp.customer.membership_type
    : null

  if (pgResp.customer) {
    const c = pgResp.customer
    data.customer = {
      origin: c.origin,
      company: c.company,
      customerId: c.customer_id,
      locationUuid: c.location_uuid,
      loanLimit: c.loan_limit,
      fsid: c.fsid,
      ordersStatus: c.orders_status,
      creditApplicationStatus: getApplicationStatus(c.credit_application, c.loan_limit).status,
      creditCustomer: !!(c.loan_limit && c.loan_limit),
      has60DayPaymentOption: get(pgResp, 'payment_options.has_60_day_option', false),
      has90DayPaymentOption: get(pgResp, 'payment_options.has_90_day_option', false),
      pendingDeliveriesAmount
    }
  }
  if (pgResp.balance) {
    const b = pgResp.balance
    data.customer = {
      ...(data.customer || {}),
      arBalance: b.ar_balance,
      lrBalance: b.lr_balance,
      pendingBalance: b.pending_balance,
      totalBalance: b.total_balance,
      cashBalance: Math.abs(b.cash_balance),
      creditBalance: get(data, 'customer.loanLimit', 0) - loanDeliveryAmountUsed,
      availableBalance: b.available_balance,
      dueAmount: 0,
      totalOutstandingAmount: 0
    }
  }
  if (pgResp.invoices) {
    data.invoices = pgResp.invoices.map(i => ({
      txnId: i.txn_id,
      docNo: i.doc_no,
      txnDate: i.txn_date,
      txnType: 'invoice',
      dueDate: i.due_date,
      amount: i.amount,
      balance: i.balance,
      currency: i.currency
    }))
  }
  if (pgResp.invoice_payments) {
    data.invoicePayments = pgResp.invoice_payments.map(p => ({
      paymentId: p.payment_id,
      invoiceId: p.invoice_id,
      txnDate: p.txn_date,
      amount: p.amount
    }))
  }
  if (pgResp.invoice_late_fees) {
    data.invoiceLateFees = pgResp.invoice_late_fees.map(f => ({
      feeInvoiceId: f.fee_invoice_id,
      overdueInvoiceId: f.overdue_invoice_id,
      amount: f.amount
    }))
  }
  if (pgResp.paymentplans) {
    data.paymentPlans = pgResp.paymentplans.map(p => ({
      paymentPlanId: p.account_id,
      invoiceId: p.invoice_id,
      balance: p.balance,
      installmentAmount: p.installment_amount,
      numInstallments: p.num_installments,
      startDate: p.start_date,
      frequency: p.frequency,
      createdAt: p.created_at,
      serviceFee: p.service_fee
    }))
  }
  if (pgResp.installments) {
    data.installments = pgResp.installments.map(i => ({
      txnId: i.id,
      txnType: 'installment',
      paymentPlanId: i.account_id,
      installmentNo: i.installment_no,
      amount: i.amount,
      balance: i.balance,
      txnDate: i.created_at,
      dueDate: i.due_date,
      serviceFee: i.service_fee,
      separateServiceFeeAndPrincipal: i.separate_service_fee_and_principal
    }))
  }
  if (pgResp.installment_payments) {
    data.installmentPayments = pgResp.installment_payments.map(p => ({
      paymentTxnId: p.txn_id,
      paymentTxnType: p.txn_type,
      paymentPlanId: p.account_id,
      installmentNo: p.installment_no,
      txnDate: p.txn_date,
      totalAmount: p.credit_amount,
      appliedAmount: p.applied_amount
    }))
  }
  if (pgResp.installment_late_fees) {
    data.installmentLateFees = pgResp.installment_late_fees.map(f => ({
      feeInvoiceId: f.fee_invoice_id,
      paymentPlanId: f.lr_account_id,
      installmentNo: f.installment_no,
      amount: f.amount
    }))
  }
  if (pgResp.transactions) {
    data.transactions = pgResp.transactions.map(t => ({
      txnType: t.txn_type,
      txnId: t.txn_id,
      txnDate: t.txn_date,
      entryType: t.entry_type,
      amount: t.amount,
      accountId: t.account_id,
      accountType: t.account_type,
      description: t.description || '',
      status: t.status || '',
      dueDate: t.due_date || ''
    }))
  }
  if (pgResp.invoice_shipments) {
    data.shipments = pgResp.invoice_shipments.map(s => ({
      invoiceId: s.invoice_id,
      shipmentId: s.shipment_id,
      orderId: s.order_id,
      paymentChoice: s.payment_choice
    }))
  }

  if (pgResp.payment_plan_service_fees) {
    data.paymentPlanServiceFees = pgResp.payment_plan_service_fees.map(s => ({
      feeInvoiceId: s.fee_invoice_id,
      loanInvoiceId: s.loan_invoice_id,
      amount: s.amount
    }))
  }

  if (pgResp.account_flows) {
    data.accountFlows = pgResp.account_flows
  }

  if (pgResp.pending_payments && data.invoices && data.installments) {
    const pendingPayments = pgResp.pending_payments
      .map(p => ({
        reference: p.reference,
        amount: p.amount,
        unappliedAmount: p.amount,
        txnDate: p.txn_date
      }))

    const { invoicePayments, installmentPayments } = applyPendingPayments(
      pendingPayments, data.invoices, data.installments
    )
    data.invoicePendingPayments = invoicePayments
    data.installmentPendingPayments = installmentPayments
  }

  if (pgResp.creditmemos) {
    data.creditMemos = pgResp.creditmemos.map(cm => ({
      txnId: cm.txn_id,
      docNo: cm.doc_no,
      txnDate: cm.txn_date,
      txnType: 'creditmemo',
      amount: cm.amount,
      balance: cm.unapplied_amount,
      currency: cm.currency
    }))
  }

  if (pgResp.creditmemo_linked_invoices) {
    data.creditMemoLinkedInvoices = pgResp.creditmemo_linked_invoices.map(cmi => ({
      invoiceId: cmi.invoice_id,
      creditMemoId: cmi.creditmemo_id,
      amount: cmi.amount,
      txnDate: cmi.txn_date
    }))
  }

  if (pgResp.creditmemo_shipments) {
    data.creditMemoShipments = pgResp.creditmemo_shipments.map(cms => ({
      creditMemoId: cms.creditmemo_id,
      returnShipmentId: cms.return_shipment_id,
      orderId: cms.order_id,
      parentShipmentId: cms.parent_shipment_id
    }))
  }

  if (data.customer && data.invoices && data.installments) {
    const { totalOutstandingAmount, dueAmount } = calculateDueAmount(data.invoices, data.installments)
    data.customer.totalOutstandingAmount = totalOutstandingAmount
    data.customer.dueAmount = dueAmount
    // subtract the total outstanding amount from the clients credit (loan limit) balance
    data.customer.creditBalance = get(data, 'customer.creditBalance', 0) - totalOutstandingAmount
  }

  if (data.customer && data.invoices && data.installments) {
    data.customer.isBlocked = isBlocked(
      data.customer.company,
      data.customer.blockingRule,
      data.invoices,
      data.installments,
      membershipType
    )
  }
  return data
}

function isBlocked (company, blockingRule, invoices, installments, membershipType) {
  if (blockingRule === 'bypass' || membershipType === MEMBERSHIPS.BASIC) {
    return false
  }
  const gracePeriodDays = 8
  const threshold = ({
    'NG': 2500,
    'KE': 1000
  })[company]
  const now = new Date()
  let numOverdueInvoices = 0
  let numOverdueInstallments = 0
  let overdueAmount = 0
  for (const i of invoices) {
    const extendedDueDate = addDays(i.dueDate, gracePeriodDays)
    if (i.balance > 0 && now >= extendedDueDate) {
      numOverdueInvoices += 1
      overdueAmount += i.balance
    }
  }
  for (const i of installments) {
    const extendedDueDate = addDays(i.dueDate, gracePeriodDays)
    if (i.balance && now >= extendedDueDate) {
      numOverdueInstallments += 1
      overdueAmount += i.balance
    }
  }
  if (blockingRule === 'legacy') {
    return (numOverdueInvoices > 1 || numOverdueInstallments > 0) &&
      (overdueAmount > threshold)
  } else {
    // blockingRule = 'default' | null
    return (numOverdueInvoices > 0 || numOverdueInstallments > 0) &&
      (overdueAmount > threshold)
  }
}

function calculateDueAmount (invoices, installments) {
  const today = new Date().toJSON().split('T')[0]
  const { totalOutstandingAmount, dueAmount } = [
    ...(invoices || []),
    ...(installments || [])
  ].reduce((acc, payable) => {
    if (payable.dueDate <= today) {
      acc.dueAmount += payable.balance
    }
    acc.totalOutstandingAmount += payable.balance
    return acc
  }, { totalOutstandingAmount: 0, dueAmount: 0 })
  return { totalOutstandingAmount, dueAmount }
}

function applyPendingPayments (pendingPayments, invoices, installments) {
  const invoicePayments = []
  const installmentPayments = []
  const payablePayments = applyPayments(pendingPayments, [
    ...invoices,
    ...installments
  ])
  for (const pp of payablePayments) {
    const { payable, payment, appliedAmount, appliedServiceFee } = pp
    payable.balance -= (appliedAmount + appliedServiceFee)
    if (payable.txnType === 'invoice') {
      invoicePayments.push({
        reference: payment.reference,
        txnDate: payment.txnDate,
        totalAmount: payment.amount,
        appliedAmount,
        invoiceId: payable.txnId
      })
    } else if (payable.txnType === 'installment') {
      installmentPayments.push({
        reference: payment.reference,
        txnDate: payment.txnDate,
        totalAmount: payment.amount,
        appliedAmount,
        appliedServiceFee,
        paymentPlanId: payable.paymentPlanId,
        installmentNo: payable.installmentNo
      })
    }
  }
  return {
    invoicePayments,
    installmentPayments
  }
}

class Wallet {
  /**
   * @param {*} pgConnection
   * @param {import('./../../shipment/data-access/data-shipment-adapter')} dataShipmentAdapter
   * @param {import('./../../order/data-access/data-order-pg-adapter')} dataOrderAdapter
   */
  constructor (pgConnection, dataShipmentAdapter, dataOrderAdapter) {
    this.pgConnection = pgConnection
    this.dataShipmentAdapter = dataShipmentAdapter
    this.dataOrderAdapter = dataOrderAdapter
  }

  async getOverview (locationId, {excludeOrderId, pgClient} = {}) {
    const client = pgClient || this.pgConnection
    const [ pendingDeliveries, pendingOrders, { rows: [row] } ] = await Promise.all([
      this.dataShipmentAdapter.getPendingDeliveries({locationId, weeks: SHIPMENT_WINDOW}, pgClient),
      this.dataOrderAdapter.getPendingOrders(locationId, {excludeOrderId, pgClient}),
      client.query(walletQuery, [locationId])
    ])
    return transformPgResponse(row, pendingDeliveries, pendingOrders)
  }

  async getStatement (locationId, startDate, endDate) {
    const args = [locationId]
    let dateFilter = ''
    if (startDate) {
      dateFilter = `txn_date >= $${args.length + 1}`
      args.push(startDate)
    }
    if (endDate) {
      dateFilter = dateFilter.concat(` ${startDate ? ' and ' : ''} txn_date <= $${args.length + 1}`)
      args.push(endDate)
    }
    const { rows: [row] } = await this.pgConnection.query(statementQuery(dateFilter), args)
    return transformPgResponse(row)
  }

  async getPayables (locationId) {
    const { rows: [row] } = await this.pgConnection.query(payablesQuery, [locationId])
    return transformPgResponse(row)
  }

  async getTransaction (locationId, transactionId) {
    const { rows } = await this.pgConnection.query(getTransactionQuery, [locationId, transactionId])
    return rows
  }

  async getInvoicePaymentPlan (transactionId, companyCode) {
    const { rows } = await this.pgConnection.query(invoicePaymentPlanQuery, [transactionId, companyCode])
    return rows[0]
  }

  async getDepositLines (accountIds, company, customerId) {
    if (!Array.isArray(accountIds)) {
      return []
    }
    const { rows } = await this.pgConnection.query(depositLinesQuery, [accountIds, company, customerId])
    return rows
  }

  async getLRSingleBookingFeeLines (accountIds, feeAccountId, companyCode, customerId) {
    if (!accountIds || !accountIds.length) {
      return []
    }
    const {query, values} = singleLRFeeBookingQuery(accountIds, feeAccountId, companyCode, customerId)
    const { rows } = await this.pgConnection.query(query, values)
    return rows
  }

  async getOverduePAYGInvoices () {
    const { rows } = await this.pgConnection.query(overduePAYGInvoicesQuery, [MEMBERSHIPS.CLASSIC_PAYG_ONLY, MEMBERSHIPS.CLASSIC, 'PAYG'])
    return rows
  }
}

module.exports = {
  Wallet
}
